import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { DashboardResponse, DashboardStatisticsResponse } from '../../data/models/dashboard.models';
import { DashboardService } from '../../data/services/dashboard.service';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import { SessionService } from '../../../../core/services/session.service';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard {
  dashboardData: DashboardResponse | null = null;
  isLoading = false;
  errorMessage = '';

  private readonly dashboardService = inject(DashboardService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly sessionService = inject(SessionService);
  private readonly globalAlertService = inject(GlobalAlertService);

  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;
  readonly selectedCompanyName = this.companyContextService.selectedCompanyName;
  readonly isSuperAdmin = this.sessionService.isSuperAdmin;
  readonly isCompanyAdmin = this.sessionService.isCompanyAdmin;

  constructor() {
    effect(() => {
      const companyId = this.selectedCompanyId();
      this.loadDashboard(companyId);
    });
  }

  loadDashboard(companyId: number): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.dashboardService.getDashboardStatistics({ companyId }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.dashboardData = this.mapToDashboardResponse(response.data);
        } else {
          this.errorMessage = response.message || 'Unable to load dashboard statistics right now.';
          this.globalAlertService.showError(this.errorMessage);
          this.dashboardData = null;
        }
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load dashboard statistics right now.';
        this.globalAlertService.showError(this.errorMessage);
        this.dashboardData = null;
        this.isLoading = false;
      }
    });
  }

  private mapToDashboardResponse(data: DashboardStatisticsResponse): DashboardResponse {
    return {
      overviewCards: [
        { title: 'Active Jobs', value: this.formatNumberValue(data.overview.activeJobs), icon: '🎁', iconClass: 'icon-purple' },
        { title: 'Images to Process', value: this.formatNumberValue(data.overview.imagesToProcess), icon: '🕒', iconClass: 'icon-orange' },
        { title: 'Completed Jobs', value: this.formatNumberValue(data.overview.completedJobs), icon: '✅', iconClass: 'icon-green' },
        { title: 'Processed Images', value: this.formatNumberValue(data.overview.processedImages), icon: '🖼️', iconClass: 'icon-indigo' },
        { title: this.isSuperAdmin() ? 'Total Sales' : 'Total Cost', value: this.formatCurrencyValue(data.overview.totalSales), icon: '💲', iconClass: 'icon-emerald' }
      ],
      startupCreditSummary: {
        availableCredits: this.formatWalletCurrencyValue(data.overview.availableCredits, 'Available Credits'),
        usedCredits: this.formatWalletCurrencyValue(data.overview.usedCredits, 'Used Credits'),
      },
      salesByService: [
        { title: 'Best Pick', value: this.formatCurrencyValue(data.salesByService.bestPick), icon: '◎', iconClass: 'icon-blue' },
        { title: 'AI Enhancement', value: this.formatCurrencyValue(data.salesByService.aiEnhancement), icon: '✦', iconClass: 'icon-purple' },
        { title: 'Cropping', value: this.formatCurrencyValue(data.salesByService.cropping), icon: '✂', iconClass: 'icon-orange' },
        { title: 'Background Extraction', value: this.formatCurrencyValue(data.salesByService.backgroundExtraction), icon: '🖼', iconClass: 'icon-green' }
      ],
      costBreakdown: [
        { title: 'Best Pick', value: this.formatCurrencyValue(data.costBreakdown.bestPick), icon: '⬡', iconClass: 'icon-red' },
        { title: 'AI Enhancement', value: this.formatCurrencyValue(data.costBreakdown.aiEnhancement), icon: '⚡', iconClass: 'icon-yellow' },
        { title: 'Cropping', value: this.formatCurrencyValue(data.costBreakdown.cropping), icon: '◱', iconClass: 'icon-pink' },
        { title: 'Background Extraction', value: this.formatCurrencyValue(data.costBreakdown.backgroundExtraction), icon: '◈', iconClass: 'icon-cyan' }
      ],
      profitBreakdown: [
        { title: 'Best Pick', value: this.formatCurrencyValue(data.profitBreakdown.bestPick), icon: '↗', iconClass: 'icon-soft-red', valueClass: data.profitBreakdown.bestPick < 0 ? 'text-negative' : 'text-positive' },
        { title: 'AI Enhancement', value: this.formatCurrencyValue(data.profitBreakdown.aiEnhancement), icon: '📊', iconClass: 'icon-soft-green', valueClass: data.profitBreakdown.aiEnhancement < 0 ? 'text-negative' : 'text-positive' },
        { title: 'Cropping', value: this.formatCurrencyValue(data.profitBreakdown.cropping), icon: '◔', iconClass: 'icon-soft-indigo', valueClass: data.profitBreakdown.cropping < 0 ? 'text-negative' : 'text-positive' },
        { title: 'Background Extraction', value: this.formatCurrencyValue(data.profitBreakdown.backgroundExtraction), icon: '∿', iconClass: 'icon-soft-purple', valueClass: data.profitBreakdown.backgroundExtraction < 0 ? 'text-negative' : 'text-positive' }
      ]
    };
  }

  private formatCurrencyValue(value: number | undefined): string {
    if (value === undefined || value === null) return '$0.00';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  private formatWalletCurrencyValue(value: number | undefined, fieldName: string): string {
    if (value === undefined || value === null || isNaN(value)) return '$0.00';
    if (value < 0) {
      console.warn(`[Dashboard] Negative value detected for wallet field ${fieldName}: ${value}. Displaying $0.00 instead.`);
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  private formatNumberValue(value: number | undefined): string {
    if (value === undefined || value === null) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  }
}